import { useState, useEffect } from 'react';

import { Image, Button } from 'react-bootstrap';
import { useStoreActions, useStoreState } from 'easy-peasy';
import PropTypes from 'prop-types';
import { Editor } from "@tinymce/tinymce-react";
import ButtonLoaderComponent from '../widgets/ButtonLoaderComponent.jsx';
import { useNavigate } from "react-router-dom";
import { useToast } from "../../hooks/useToast.jsx";
import SelectMultipleClientsComponent from '../widgets/selectClient/SelectMultipleClientsComponent.jsx';
import HelperMetiers360 from '../../services/HelpersMetiers360.js';
import CustomSelectMultipleComponent from '../widgets/CustomSelectComponent/CustomSelectMultipleComponent.jsx';
import JSONEditor from './JSONEditor.jsx';
import { showDuration } from './ActivityDetailsComponent.jsx';

import "./ActivityFormComponent.scss";

const ActivityFormComponent = ({ existingActivity = null }) => {

    const { createActivity, updateActivity } = useStoreActions(actions => actions.activity);
    const { apiData: { BACKOFFICE_DEFAULT_VALUES } } = useStoreState(state => state.actionSlugs);
    const { fileContraints, activityAudienceTypes } = BACKOFFICE_DEFAULT_VALUES;

    const [isSending, setIsSending] = useState(false);
    const [errors, setErrors] = useState({});
    const [thumbnailFileInputName, setThumbnailFileInputName] = useState(existingActivity?.thumbnail ? 'Changer la vignette' : 'Choisir le fichier');

    const [newActivity, setNewActivity] = useState(existingActivity ? { ...existingActivity } : {});
    const [activityJSON, setActivityJSON] = useState([]);

    const [titleLenght, setTitleLenght] = useState(existingActivity?.title?.length ?? 0);
    const [selectedClients, setSelectedClients] = useState(newActivity?.clientsListData?.map(c => c.uniqueId).map(String) ?? []);
    const [selectedAudienceType, setSelectedAudienceType] = useState(existingActivity?.audienceType ?? []);

    const activitiesSlugs = useStoreState((state) => state.actionSlugs.actionSlugsDispatcher("activity"));

    const navigate = useNavigate();
    const toast = useToast();

    const MAX_TITLE_LENGTH = 80;

    const handleChange = (e) => {
        setErrors({});
        const { name, value } = e.target;
        if (name === "title") {
            setTitleLenght(value.length);
        }
        setNewActivity({ ...newActivity, [name]: value });
    }

    const handleEditorChangeDescription = (description) => {
        setErrors({});
        setNewActivity({ ...newActivity, description: description });
    }

    const validateForm = () => {
        let result = true;
        setErrors({});
        let errorsList = {};
        if (!newActivity.title) {
            errorsList.title = "Merci d'indiquer un nom";
            result = false;
        }
        if (!newActivity.description) {
            errorsList.description = "Merci d'indiquer une description";
            result = false;
        }
        if (newActivity.title?.length > MAX_TITLE_LENGTH) {
            errorsList.title = "Le nom est trop long";
            result = false;
        }
        if (!newActivity.duration) {
            errorsList.duration = "Merci d'indiquer une durée";
            result = false;
        }
        if (!newActivity.slug) {
            errorsList.slug = "Merci d'indiquer un slug";
            result = false;
        }

        let thumbnail = document.querySelector('input[name="thumbnailSource"]').files[0];

        if (!thumbnail && !existingActivity?.thumbnail) {
            errorsList.thumbnail = "Merci d'inclure une vignette";
            result = false;
        }

        if (thumbnail && thumbnail.size) {
            if (thumbnail.size > fileContraints.MAX_SIZE_IMAGE_BYTES) {
                errorsList['thumbnail'] = "Vignette trop volumineuse";
            }
            if (!fileContraints.IMAGE_MIME_TYPES.includes(thumbnail.type)) {
                errorsList['thumbnail'] = "Format de la vignette non autorisé";
            }
        }
        setErrors(errorsList);
        Object.keys(errorsList)?.length > 0 && toast.open({ message: Object.values(errorsList)?.join(", "), variant: "danger" });

        return result;
    }


    const handleClick = () => {
        const isValidated = validateForm();

        if (isValidated) {

            const formData = new FormData();
            formData.append('title', newActivity.title);
            formData.append('description', newActivity.description);
            formData.append('slug', newActivity.slug);
            formData.append('duration', newActivity.duration);
            selectedAudienceType?.length > 0 && formData.append('audienceType', JSON.stringify(selectedAudienceType));

            activityJSON?.length > 0 ?
                formData.append('activityJSON', JSON.stringify(Object.fromEntries(activityJSON.map(({ key, value }) => [key, value]))))
                : formData.append('activityJSON', null);

            if (document.querySelector('input[name="thumbnailSource"]').files[0]) {
                formData.append('thumbnailSource', document.querySelector('input[name="thumbnailSource"]').files[0]);
            }

            selectedClients.forEach(uuid => {
                formData.append('clientsList[]', uuid);
            });

            setIsSending(true);
            if (existingActivity) {
                updateActivity({ formData, uniqueId: existingActivity?.uniqueId })
                    .then((res) => {
                        setIsSending(false);
                        toast.open({ message: "Activité modifiée", variant: "success" });
                        navigate(activitiesSlugs.readOne.replace(':uniqueId', existingActivity?.uniqueId));
                    })
                    .catch((error) => {
                        setIsSending(false);
                    })
            } else {
                createActivity({ formData })
                    .then((res) => {
                        setIsSending(false);
                        toast.open({ message: "Activité créé", variant: "success" });
                        navigate(activitiesSlugs.readAll);
                    })
                    .catch((error) => {
                        setIsSending(false);
                    })
            }
        }
    }

    const handleDuration = (increase) => {
        setErrors({});
        if (increase && (!newActivity?.duration || newActivity?.duration < 3)) {
            const newDuration = (newActivity.duration ?? 0) + 1;
            setNewActivity({ ...newActivity, duration: newDuration });
            return;
        }
        if (!increase && newActivity?.duration > 0) {
            const newDuration = newActivity.duration - 1;
            setNewActivity({ ...newActivity, duration: newDuration });
            return;
        }
    }
    useEffect(() => {
        if (existingActivity?.activityJSON) {
            const jsonAsArray = Object.entries(existingActivity?.activityJSON).map(([key, value], index) => (
                {
                    id: index,
                    key: key,
                    value: value
                }
            ));
            setActivityJSON(jsonAsArray);
        }
    }, [existingActivity]);

    return (
        <div>
            <label htmlFor='title' className='mr-2'>Nom</label>
            <input type="text" name='title' id='title' defaultValue={newActivity.title} onChange={handleChange} style={{ minWidth: "40em" }}></input>
            {!!titleLenght && <span className='ml-2 textLimit' style={{ color: titleLenght > MAX_TITLE_LENGTH ? "red" : "black" }}>{titleLenght}/{MAX_TITLE_LENGTH}</span>}
            {errors?.title && <p className='text-danger'>{errors.title}</p>}
            <hr />
            <label>Publics cibles</label>
            <div className='searchblock'>
                <CustomSelectMultipleComponent
                    options={activityAudienceTypes?.map(at => { return ({ ...at, label: HelperMetiers360.capitalizeFirstLetter(at.label) }) })}
                    selectedOptions={selectedAudienceType}
                    selectOptions={setSelectedAudienceType}
                    optionLabel="label"
                    valueLabel="value"
                />
            </div>

            <hr />
                <label>Description</label>
                <Editor
                    name="description"
                    apiKey={process.env.REACT_APP_TINYMCE_KEY}
                    init={{
                        height: 300,
                        menubar: false,
                        plugins: [
                            'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview', 'anchor',
                            'searchreplace', 'visualblocks', 'code', 'fullscreen',
                            'insertdatetime', 'media', 'table', "code", 'help', 'wordcount', 'emoticons'
                        ],
                        toolbar:
                            'undo redo | bold italic backcolor | \
                        alignleft aligncenter alignright alignjustify | \
                        bullist numlist outdent indent | charmap emoticons | link | removeformat | help'
                    }}
                    onEditorChange={handleEditorChangeDescription}
                    initialValue={existingActivity ? existingActivity.description : ' '}
                />
                {errors?.description && <p className='text-danger'>{errors.description}</p>}


            <hr />
                <label>Vignette ({fileContraints.IMAGE_MIME_TYPES_NAMES?.join(',')})  ({fileContraints.MAX_SIZE_IMAGE_MO} Mo max.)</label>
                <div className="input-group mb-4">
                    <div className="input-group-prepend">
                        <span className="input-group-text" id="inputGroupFileAddon01">
                            {existingActivity && existingActivity.thumbnail ? <Image src={existingActivity.thumbnail} rounded thumbnail className={"max-width-200"} /> : 'Choisir la vignette'}
                        </span>
                    </div>
                    <div className="custom-file">
                        <input
                            type="file"
                            className="custom-file-input"
                            name="thumbnailSource"
                            aria-describedby="inputGroupFileAddon01"
                            accept={fileContraints.IMAGE_MIME_TYPES?.join(',')}
                            onChange={(event) => setThumbnailFileInputName(event.target.files[0].name)}
                        />
                        <label className="custom-file-label" htmlFor="inputGroupFile01" data-browse="Parcourir">
                            {thumbnailFileInputName}
                        </label>
                    </div>
            </div>
            {errors?.thumbnail && <p className='text-danger'>{errors.thumbnail}</p>}

            <hr />
            <label htmlFor='duration' className='mr-2'>Durée</label>
            <div className="mb-4">
                <Button className="mr-1" variant='light' onClick={() => handleDuration(false)} disabled={!newActivity.duration || newActivity?.duration < 1}>-</Button>
                <Button variant='light' onClick={() => handleDuration(true)} disabled={newActivity?.duration > 2}>+</Button>
                <span className='ml-2' dangerouslySetInnerHTML={{ __html: showDuration(newActivity, true) }} />
            </div>
            {errors?.duration && <p className='text-danger'>{errors.duration}</p>}
            <hr />
            <label>Liste clients restreinte</label>
            <p className='text-danger'>&#9888; Si le client est tête de réseau l'ensemble de son réseau aura accès à l'activité</p>
                <SelectMultipleClientsComponent
                    nameSelect={`clientsList`}
                    selectedClients={selectedClients && selectedClients.length ? selectedClients : []}
                    setSelectedClients={setSelectedClients} />


            <hr />
            <label htmlFor='slug' className='mr-2'>Slug (url)</label>
            <input type="text" name='slug' id='slug' placeholder="Réservé à l'équipe dev" defaultValue={newActivity.slug} onChange={handleChange} style={{ minWidth: "40em" }}></input>
            {errors?.slug && <p className='text-danger'>{errors.slug}</p>}
            <hr />
            <label htmlFor='activityJSON' className='mr-2'>Objet lié à l'activité (dev)</label>
            <JSONEditor json={activityJSON} setJson={setActivityJSON} />

            <ButtonLoaderComponent
                className='mt-4'
                variant={existingActivity ? "secondary" : "success"}
                onClick={handleClick}
                msg={existingActivity ? "Éditer" : "Ajouter"}
                iconClassName="fas fa-save"
                isSending={isSending}
                disabled={isSending}
            />
        </div>
    )
}

ActivityFormComponent.propTypes = {
    existingActivity: PropTypes.object,
};

export default ActivityFormComponent